import { Platform } from '@angular/cdk/platform';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  Renderer2,
  inject,
  numberAttribute,
} from '@angular/core';

type ShowDirection = 'right' | 'left' | 'up' | 'down';

@Directive()
export abstract class ShowOnEnterDirective implements AfterViewInit, OnDestroy {
  @Input({ transform: numberAttribute }) showDuration: number = 500;
  @Input({ transform: numberAttribute }) showDelay: number = 0;
  @Input({ transform: numberAttribute }) showDistance: number = 20;
  @Input({ transform: numberAttribute }) showThreshold: number = 0.1;
  @Input() showRootMargin: string = '0px 0px -10% 0px';

  protected abstract readonly direction: ShowDirection | null;

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly platform = inject(Platform);
  private readonly renderer = inject(Renderer2);

  private observer?: IntersectionObserver;
  private player?: Animation;
  private originalOpacity: string = '';
  private originalTransform: string = '';

  constructor() {
    if (!this.platform.isBrowser) {
      return;
    }

    this.originalOpacity = this.el.nativeElement.style.opacity;
    this.originalTransform = this.el.nativeElement.style.transform;
    this.renderer.setStyle(this.el.nativeElement, 'opacity', '0');
    this.renderer.setStyle(this.el.nativeElement, 'will-change', 'opacity, transform');
  }

  ngAfterViewInit(): void {
    if (!this.platform.isBrowser) {
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.restoreOriginalStyles();
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.applyAnimation();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        this.observer?.disconnect();
        this.observer = undefined;
        this.applyAnimation();
      },
      {
        rootMargin: this.showRootMargin,
        threshold: this.normalizedThreshold,
      }
    );

    this.observer.observe(this.el.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.player?.cancel();
  }

  private applyAnimation(): void {
    if (typeof this.el.nativeElement.animate !== 'function') {
      this.restoreOriginalStyles();
      return;
    }

    this.player = this.el.nativeElement.animate(
      [this.startStyle, this.endStyle],
      {
        duration: Math.max(this.showDuration, 0),
        delay: this.showDelay,
        easing: 'ease-in-out',
        fill: 'both',
      }
    );

    this.player.onfinish = () => {
      const player = this.player;
      this.restoreOriginalStyles();
      this.player = undefined;
      player?.cancel();
    };
    this.player.oncancel = () => {
      this.player = undefined;
    };
  }

  private restoreOriginalStyles(): void {
    this.setOrRemoveStyle('opacity', this.originalOpacity);
    if (this.usesTransform) {
      this.setOrRemoveStyle('transform', this.originalTransform);
    }
    this.renderer.removeStyle(this.el.nativeElement, 'will-change');
  }

  private setOrRemoveStyle(name: string, value: string): void {
    if (value) {
      this.renderer.setStyle(this.el.nativeElement, name, value);
      return;
    }

    this.renderer.removeStyle(this.el.nativeElement, name);
  }

  private get startStyle(): Keyframe {
    const transform = this.initialTransform;

    if (transform) {
      return { opacity: 0, transform };
    }

    return { opacity: 0 };
  }

  private get endStyle(): Keyframe {
    if (this.usesTransform) {
      return {
        opacity: 1,
        transform: this.originalTransform || 'translate(0, 0)',
      };
    }

    return { opacity: 1 };
  }

  private get initialTransform(): string | null {
    if (!this.direction) {
      return null;
    }

    const distance = Math.abs(this.showDistance);
    const offset = this.directionOffset(distance, this.direction);

    if (!this.originalTransform) {
      return offset;
    }

    return `${offset} ${this.originalTransform}`;
  }

  private directionOffset(distance: number, direction: ShowDirection): string {
    switch (direction) {
      case 'right':
        return `translateX(${distance}px)`;
      case 'left':
        return `translateX(-${distance}px)`;
      case 'up':
        return `translateY(-${distance}px)`;
      case 'down':
        return `translateY(${distance}px)`;
    }
  }

  private get usesTransform(): boolean {
    return this.direction !== null;
  }

  private get normalizedThreshold(): number {
    return Math.min(Math.max(this.showThreshold, 0), 1);
  }
}
